import { useEffect, useState } from 'react'
import styles from './CleanlinessRubricPanel.module.css'
import { fetchData } from '../../../../global/utils/fetch'
import PrimaryButton from '../../../../global/components/buttons/primary-button/PrimaryButton'
import { ICleanlinessPromptConfigResponse } from '../../../../../../app/types/cleanliness-score'
import { getCleanlinessPromptConfig } from '../hooks/api'

interface ICleanlinessRubricPanelProps {
  onUseBuiltInRubric: (promptText: string) => void
}

/**
 * Left-hand panel for the van cleanliness rubric entry on the AI Training
 * tab: what the prompt grades, how an override takes effect, and the
 * built-in rubric the nightly job falls back to.
 */
function CleanlinessRubricPanel({
  onUseBuiltInRubric,
}: ICleanlinessRubricPanelProps) {
  const [config, setConfig] = useState<ICleanlinessPromptConfigResponse | null>(
    null,
  )
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    const abortController = new AbortController()
    fetchData<ICleanlinessPromptConfigResponse>(
      getCleanlinessPromptConfig(),
      abortController.signal,
    )
      .then(setConfig)
      .catch((error) => {
        if (abortController.signal.aborted) return
        console.error('Failed to load the cleanliness rubric:', error)
        setLoadFailed(true)
      })
    return () => abortController.abort()
  }, [])

  if (loadFailed) {
    return (
      <div className={styles.panel}>
        <p className={styles.text}>
          The built-in rubric could not be loaded. The editor on the right still
          works.
        </p>
      </div>
    )
  }

  if (!config) {
    return (
      <div className={styles.panel}>
        <p className={styles.muted}>Loading the rubric...</p>
      </div>
    )
  }

  return (
    <div className={styles.panel}>
      <div>
        <h3 className={styles.sectionLabel}>What this prompt does</h3>
        <p className={styles.text}>
          Grades each interior photo on a DVIR from 0 to 100 for cleanliness.
          The nightly job averages the photos into an inspection score, and the
          Van Cleanliness tab averages inspections over the last 30 days. One
          call per photo, for these sections:
        </p>
        <ul className={styles.list}>
          {config.scoredSections.map((section) => (
            <li key={section.sectionKey}>{section.title}</li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className={styles.sectionLabel}>How the override works</h3>
        <p className={styles.text}>
          Save and enable a prompt here and the nightly job and backfills use
          it, with the model chosen on the right, instead of the built-in
          rubric. Disable it to go back to the built-in rubric.
        </p>
        <p className={styles.text}>
          There is no pass/fail test for this prompt. Judge changes by
          re-scoring a few audits and reviewing the photos and reasons on the
          Van Cleanliness tab.
        </p>
        <p className={styles.status}>
          {config.overrideIsEnabled
            ? `In use now: the override saved here, on ${config.overrideModel ?? 'the default model'}`
            : `In use now: the built-in rubric (version ${config.rubricVersion}) on ${config.defaultModel}`}
        </p>
      </div>

      <div>
        <h3 className={styles.sectionLabel}>Built-in rubric</h3>
        <pre className={styles.rubric}>{config.defaultPromptText}</pre>
        <PrimaryButton
          buttonName='Copy the built-in rubric into the editor'
          onClick={() => onUseBuiltInRubric(config.defaultPromptText)}
        />
      </div>

      <div>
        <h3 className={styles.sectionLabel}>Answer format (fixed)</h3>
        <p className={styles.text}>
          The system separately asks the model for a JSON answer with a scorable
          flag, a whole-number score, and a one-sentence reason. The prompt only
          needs to describe what to grade and where the lines are.
        </p>
      </div>
    </div>
  )
}

export default CleanlinessRubricPanel
